// StateMachine + StateMachineMgr — message routing, delayed-message FIFO, state-change
// pump, and the per-message TrackState (a determinism-contract surface).
// game/StateMachine.cpp + game/ht.h:2190-2269.

import { StateFrame } from '../hash/stateframe.ts';
import { quad } from '../hash/quads.ts';
import type { SimContext, TickSource } from '../context.ts';
import { UpdateInterval } from './updateinterval.ts';
import {
  Message,
  cloneMessage,
  newMessage,
  ksmidNull,
  knHandled,
  knNotHandled,
  knDeleted,
  kstReservedGlobal,
  kmidReservedEnter,
  kmidReservedExit,
  kmidHit,
  kmidPlaySfx,
  kmidEnemyNearby,
  kmidMoveCommand,
  kmidAttackCommand,
  kmidUpgradeCommand,
  kmidBuildOtherCommand,
  kmidAbortBuildOtherCommand,
  kmidDeliverCommand,
  kmidMineCommand,
  kmidGuardAreaAction,
  kmidHuntEnemiesAction,
  kmidPlayerDisconnect,
  encodeMessageMem,
  decodeMessageMem,
} from './message.ts';

// game/ht.h:2190-2231
export abstract class StateMachine {
  m_unvl = new UpdateInterval();
  m_st = kstReservedGlobal; // ctor: m_st = m_stNext = kstReservedGlobal
  m_stNext = kstReservedGlobal;
  m_fForceStateChange = false;

  // game/StateMachine.cpp:19-22 — base handler
  ProcessStateMachineMessage(_st: number, _pmsg: Message): number {
    return knHandled;
  }

  GetUpdateInterval(): UpdateInterval {
    return this.m_unvl;
  }

  // ht.h:2207-2209
  protected ProcessMessage(st: number, pmsg: Message): number {
    return this.ProcessStateMachineMessage(st, pmsg);
  }

  /** The SetState macro body (ht.h:2177): callable from any handler. */
  protected SetState(stNext: number): void {
    this.m_stNext = stNext;
    this.m_fForceStateChange = true;
    this.m_unvl.MinSkip();
  }

  // exposed for the mgr (friend class StateMachineMgr in C++)
  _processMessage(st: number, pmsg: Message): number {
    return this.ProcessMessage(st, pmsg);
  }
}

interface DelayedMessage {
  // ht.h:2138-2142
  msg: Message;
  pdmsgNext: DelayedMessage | null;
}

// game/StateMachine.cpp:341-380 — indexed by MessageId; the 'MUPC' duplicate (sim-quirks A8; indices 11
// and 22) is in the shipping table and therefore part of the hash contract.
const gmpmidQuad: number[] = [
  'MRNL',
  'MREN',
  'MREX',
  'MRUP',
  'MHIT',
  'MNAH',
  'MDEL',
  'MENB',
  'MPLH',
  'MMWN',
  'MBUP',
  'MUPC',
  'MMVC',
  'MATC',
  'MANC',
  'MBOC',
  'MABO',
  'MBDC',
  'MFRC',
  'MSSM',
  'MSDC',
  'MREC',
  'MUPC',
  'MAUC',
  'MTFC',
  'MDLC',
  'MGXD',
  'MMIC',
  'MMAC',
  'MAAC',
  'MGUA',
  'MGVA',
  'MGAA',
  'MHEA',
  'MFIR',
  'MHET',
  'MSFX',
  'PDIS',
].map(quad);

export abstract class StateMachineMgr {
  // The delayed list (StateMachine.cpp:28 m_pdmsgHead) behind a permanent sentinel node:
  // the C++ walks with a pointer-to-pointer (&m_pdmsgHead or &node->pdmsgNext); the
  // sentinel gives TS the same uniform "slot" so unlink-during-walk and append-during-
  // route behave identically.
  private m_head: { pdmsgNext: DelayedMessage | null } = { pdmsgNext: null };
  private m_ptimm!: TickSource;
  protected ctx: SimContext;

  constructor(ctx: SimContext) {
    this.ctx = ctx;
  }

  abstract GetId(psm: StateMachine): number;
  abstract GetStateMachine(smid: number): StateMachine | null;

  // game/StateMachine.cpp:36-41
  Init(ptimm: TickSource): boolean {
    this.m_ptimm = ptimm;
    this.ClearDelayedMessages();
    return true;
  }

  // game/StateMachine.cpp:96-105
  ClearDelayedMessages(): void {
    this.m_head.pdmsgNext = null;
  }

  // game/StateMachine.cpp:107-111
  SendMsg(pmsg: Message): void {
    pmsg.tDelivery = 0;
    this.RouteMessage(pmsg);
  }

  // game/StateMachine.cpp:113-123
  SendMsgIds(mid: number, smidReceiver: number): void {
    const msg = newMessage(mid, ksmidNull, smidReceiver);
    this.RouteMessage(msg);
  }

  // game/StateMachine.cpp:125-135
  SendMsgFrom(mid: number, smidSender: number, smidReceiver: number): void {
    this.RouteMessage(newMessage(mid, smidSender, smidReceiver));
  }

  // game/StateMachine.cpp:137-141
  // game/StateMachine.cpp:45-77 (knVerStateMachineMgrState 1) — delayed messages
  // round-trip as raw 28-byte images + a delivery-time DELTA from the current tick
  LoadState(pstm: import('./stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    let cmsgs = pstm.ReadWord();
    const tCurrent = this.m_ptimm.GetTickCount();
    while (cmsgs-- !== 0) {
      const msg = decodeMessageMem(pstm.Read(28).slice());
      const ctDelivery = pstm.ReadDword();
      msg.tDelivery = (tCurrent + ctDelivery) | 0;
      this.StoreDelayedMessage(msg);
    }
    return pstm.IsSuccess();
  }

  SaveState(pstm: import('./stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    let cmsgs = 0;
    for (let p = this.m_head.pdmsgNext; p !== null; p = p.pdmsgNext) cmsgs++;
    pstm.WriteWord(cmsgs);
    const tCurrent = this.m_ptimm.GetTickCount();
    for (let p = this.m_head.pdmsgNext; p !== null; p = p.pdmsgNext) {
      pstm.Write(encodeMessageMem(p.msg));
      {
        // dev hook: message-image span for the cross-validation masker (see mobileunit.ts)
        const ps = pstm as unknown as {
          tellWrite?: () => number;
          msgSpans?: { start: number; end: number }[];
        };
        if (ps.msgSpans !== undefined && ps.tellWrite !== undefined) {
          const end = ps.tellWrite();
          ps.msgSpans.push({ start: end - 28, end });
        }
      }
      pstm.WriteDword((p.msg.tDelivery - tCurrent) | 0);
    }
    return pstm.IsSuccess();
  }

  PostMsg(pmsg: Message): void {
    pmsg.tDelivery = 0;
    this.StoreDelayedMessage(pmsg);
  }

  // game/StateMachine.cpp:143-154
  PostMsgIds(mid: number, smidSender: number, smidReceiver: number): void {
    const msg = newMessage(mid, smidSender, smidReceiver);
    msg.tDelivery = 0;
    this.StoreDelayedMessage(msg);
  }

  // game/StateMachine.cpp:156-160
  SendDelayedMsg(pmsg: Message, tDelay: number): void {
    pmsg.tDelivery = (this.m_ptimm.GetTickCount() + tDelay) | 0;
    this.RouteMessage(pmsg);
  }

  // game/StateMachine.cpp:162-173
  SendDelayedMsgIds(mid: number, tDelay: number, smidSender: number, smidReceiver: number): void {
    const msg = newMessage(mid, smidSender, smidReceiver);
    msg.tDelivery = (this.m_ptimm.GetTickCount() + tDelay) | 0;
    this.RouteMessage(msg);
  }

  // game/StateMachine.cpp:175-237
  RouteMessage(pmsg: Message): void {
    this.ctx.cMessagesPerUpdate++; // extern int gcMessagesPerUpdate

    // TRACKSTATE: track that this message has been sent (StateMachine.cpp:180-186)
    if (this.ctx.frameCurrent !== null) {
      this.TrackState(this.ctx.frameCurrent, pmsg);
    }

    const psm = this.GetStateMachine(pmsg.smidReceiver);
    if (psm === null) return; // receiver gone — discard

    if (pmsg.tDelivery > this.m_ptimm.GetTickCount()) {
      this.StoreDelayedMessage(pmsg);
      return;
    }

    if (psm.m_fForceStateChange) this.ProcessStateChange(psm);

    let nRet = psm._processMessage(psm.m_st, pmsg);
    if (nRet === knDeleted) return;

    if (nRet === knNotHandled) {
      nRet = psm._processMessage(kstReservedGlobal, pmsg);
    }
    if (nRet === knDeleted) return;

    if (psm.m_fForceStateChange) this.ProcessStateChange(psm);
  }

  // game/StateMachine.cpp:239-290 — the Enter/Exit pump. Enter/Exit deliberately do NOT
  // bubble to the Global handler; Exit handlers may not change state.
  private ProcessStateChange(psm: StateMachine): void {
    while (psm.m_fForceStateChange) {
      const msgT = new Message();
      msgT.smidSender = msgT.smidReceiver = this.GetId(psm);
      msgT.tDelivery = 0;

      psm.m_fForceStateChange = false;

      msgT.mid = kmidReservedExit;
      let nRet = psm._processMessage(psm.m_st, msgT);
      if (nRet === knDeleted) return;

      psm.m_fForceStateChange = false;

      psm.m_st = psm.m_stNext;

      msgT.mid = kmidReservedEnter;
      nRet = psm._processMessage(psm.m_st, msgT);
      if (nRet === knDeleted) return;
    }
  }

  // game/StateMachine.cpp:294-308 — pointer-to-pointer walk: unlink due messages (write
  // through the slot BEFORE routing), advance past not-due ones. Messages appended to the
  // tail DURING a route are visited in the same pass — exactly the C++ traversal.
  DispatchDelayedMessages(): void {
    let p: { pdmsgNext: DelayedMessage | null } = this.m_head;
    while (p.pdmsgNext !== null) {
      const pdmsg = p.pdmsgNext;
      if (pdmsg.msg.tDelivery <= this.m_ptimm.GetTickCount()) {
        p.pdmsgNext = pdmsg.pdmsgNext; // *ppdmsg = pdmsg->pdmsgNext (unlink first)
        this.RouteMessage(pdmsg.msg);
      } else {
        p = pdmsg; // ppdmsg = &(*ppdmsg)->pdmsgNext
      }
    }
  }

  // game/StateMachine.cpp:310-337 — append at TAIL (FIFO at equal delivery time)
  private StoreDelayedMessage(pmsg: Message): boolean {
    const pdmsg: DelayedMessage = { msg: cloneMessage(pmsg), pdmsgNext: null };
    let p: { pdmsgNext: DelayedMessage | null } = this.m_head;
    while (p.pdmsgNext !== null) p = p.pdmsgNext;
    p.pdmsgNext = pdmsg;
    return true;
  }

  /** Delayed-message count (SaveState's walk; also handy for tests/diagnostics). */
  CountDelayedMessages(): number {
    let n = 0;
    for (let p = this.m_head.pdmsgNext; p !== null; p = p.pdmsgNext) n++;
    return n;
  }

  // game/StateMachine.cpp:382-468 — the per-message hash contract. 'SFX ' deliberately
  // tracks 0 (the sfx is picked with the ASYNC rng); every (dword) cast is u32 via
  // AddValue's own coercion.
  private TrackState(frame: StateFrame, pmsg: Message): void {
    const i = frame.AddCountedValue(gmpmidQuad[pmsg.mid]);
    frame.AddValue(quad('SMIS'), pmsg.smidSender, i);
    frame.AddValue(quad('SMIR'), pmsg.smidReceiver, i);

    switch (pmsg.mid) {
      case kmidHit:
        frame.AddValue(quad('GIDA'), pmsg.Hit.gidAssailant, i);
        frame.AddValue(quad('SIDA'), pmsg.Hit.sideAssailant, i);
        frame.AddValue(quad('NDAM'), pmsg.Hit.nDamage, i);
        break;

      case kmidPlaySfx:
        frame.AddValue(quad('SFX '), 0, i);
        break;

      case kmidEnemyNearby:
        frame.AddValue(quad('ENNB'), pmsg.EnemyNearby.gidEnemy, i);
        break;

      case kmidMoveCommand:
        frame.AddValue(quad('GIDT'), pmsg.MoveCommand.gidTarget, i);
        frame.AddValue(quad('TRWX'), pmsg.MoveCommand.wptTarget.wx, i);
        frame.AddValue(quad('TRWY'), pmsg.MoveCommand.wptTarget.wy, i);
        frame.AddValue(quad('TCWX'), pmsg.MoveCommand.wptTargetCenter.wx, i);
        frame.AddValue(quad('TCWY'), pmsg.MoveCommand.wptTargetCenter.wy, i);
        frame.AddValue(quad('TCRA'), pmsg.MoveCommand.tcTargetRadius, i);
        frame.AddValue(quad('MDPU'), pmsg.MoveCommand.wcMoveDistPerUpdate, i);
        break;

      case kmidAttackCommand:
        frame.AddValue(quad('GIDT'), pmsg.AttackCommand.gidTarget, i);
        frame.AddValue(quad('TRWX'), pmsg.AttackCommand.wptTarget.wx, i);
        frame.AddValue(quad('TRWY'), pmsg.AttackCommand.wptTarget.wy, i);
        frame.AddValue(quad('TCWX'), pmsg.AttackCommand.wptTargetCenter.wx, i);
        frame.AddValue(quad('TCWY'), pmsg.AttackCommand.wptTargetCenter.wy, i);
        frame.AddValue(quad('TCRA'), pmsg.AttackCommand.tcTargetRadius, i);
        frame.AddValue(quad('MDPU'), pmsg.AttackCommand.wcMoveDistPerUpdate, i);
        break;

      case kmidUpgradeCommand:
        frame.AddValue(quad('WFUP'), pmsg.UpgradeCommand.wfUpgrade, i);
        break;

      case kmidBuildOtherCommand:
        frame.AddValue(quad('UNTT'), pmsg.BuildOtherCommand.ut, i);
        frame.AddValue(quad('WPWX'), pmsg.BuildOtherCommand.wpt.wx, i);
        frame.AddValue(quad('WPWY'), pmsg.BuildOtherCommand.wpt.wy, i);
        break;

      case kmidAbortBuildOtherCommand:
        frame.AddValue(quad('UNTT'), pmsg.AbortBuildOtherCommand.ut, i);
        break;

      case kmidDeliverCommand:
        frame.AddValue(quad('GTAR'), pmsg.DeliverCommand.gidTarget, i);
        break;

      case kmidMineCommand:
        frame.AddValue(quad('GTAR'), pmsg.MineCommand.gidTarget, i);
        frame.AddValue(quad('TRWX'), pmsg.MineCommand.wptTarget.wx, i);
        frame.AddValue(quad('TRWY'), pmsg.MineCommand.wptTarget.wy, i);
        break;

      case kmidGuardAreaAction:
        frame.AddValue(quad('AREA'), pmsg.GuardAreaCommand.nArea, i);
        break;

      case kmidHuntEnemiesAction:
        frame.AddValue(quad('UMSK'), pmsg.HuntEnemiesCommand.um, i);
        break;

      case kmidPlayerDisconnect:
        frame.AddValue(quad('PID '), pmsg.PlayerDisconnect.pid, i);
        frame.AddValue(quad('REAS'), pmsg.PlayerDisconnect.nReason, i);
        break;
    }
  }
}
